import { Box, FormControl } from '@mui/material'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { getOrganizationsOfEntities } from '../../../services/entityServices'
import SearchInAPIAndMultiSelectInput from '../../Common/SearchInAPIAndMultiSelectInput'
import { useTranslation } from 'react-i18next'
import {
    IOrganizationColumnFilter,
    OrgColumnFilterAPIResponseData,
} from '../interfaces'

export const OrganizationColumnFilter = (
    props: IOrganizationColumnFilter,
): ReactElement => {
    const {
        id,
        label,
        value,
        onChange,
        templateId,
        orgId,
        fetchAllOrgsForOrgFilter,
    } = props

    const [organizations, setOrganizations] = useState<
        OrgColumnFilterAPIResponseData[]
    >([])
    const [selectedValues, setSelectedValues] = useState<any[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [searchText, setSearchText] = useState<string>('')
    const [pageNumber, setPageNumber] = useState<number>(0)
    const [hasMore, setHasMore] = useState<boolean>(false)

    const pageSize = 30

    const { t } = useTranslation()

    const onChangeRecord = useCallback(
        (id: string, selectedList: any[]) => {
            setSelectedValues(selectedList)
            onChange(id, selectedList)
        },
        [onChange],
    )

    const onSelectRecords = useCallback(
        (id: string, items: any) => {
            onChangeRecord(id, items)
        },
        [onChangeRecord],
    )

    const onSearch = useCallback((id: string, text: any) => {
        setSearchText(text)
        setPageNumber(0)
    }, [])

    const handleFetchNextPage = useCallback(() => {
        if (!isLoading && hasMore) {
            setPageNumber((prev) => prev + 1)
        }
    }, [isLoading, hasMore])

    useEffect(() => {
        setSelectedValues(value)
    }, [value])

    useEffect(() => {
        if (templateId && orgId) {
            setIsLoading(pageNumber === 0)
            const getRecords = async () => {
                try {
                    const orgsResponse = await getOrganizationsOfEntities({
                        orgId,
                        templateId,
                        pageNo: pageNumber,
                        pageSize,
                        searchText: searchText?.trim(),
                        fetchAll: fetchAllOrgsForOrgFilter,
                    })
                    const totalFetched = (pageNumber + 1) * pageSize
                    const hasMore =
                        orgsResponse.metaData.totalCount > totalFetched
                    setOrganizations((prevOrgs) =>
                        pageNumber === 0
                            ? orgsResponse.data
                            : [...prevOrgs, ...orgsResponse.data],
                    )
                    setHasMore(hasMore)
                } finally {
                    setIsLoading(false)
                }
            }
            getRecords()
        }
    }, [templateId, orgId, pageNumber, searchText, fetchAllOrgsForOrgFilter])

    return (
        <Box>
            <FormControl fullWidth size="small">
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: `100%`,
                        }}
                    >
                        <SearchInAPIAndMultiSelectInput
                            id={id}
                            label={label}
                            placeholder={t('selectLabel', { label })}
                            value={selectedValues}
                            list={organizations}
                            onSelect={onSelectRecords}
                            onSearch={onSearch}
                            onFetchNextPage={handleFetchNextPage}
                            hasMore={hasMore}
                            isFetching={isLoading}
                            itemId="orgId"
                            itemName="name"
                            autoFocusOnMount={true}
                            showLabel={false}
                            stopKeyboardPropagation={true}
                        />
                    </Box>
                </Box>
            </FormControl>
        </Box>
    )
}
